import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class NetworkDiagramClusteringService {

  createCluster(diagram, data, nodeId, typeToConnect, getClusteredNodePropertiesByNodes, getClusteredEdgePropertiesByEdges, clusterName) {
    const joinCondition = (nodeOptions) => {
      return nodeOptions.shapeType === typeToConnect && diagram.getConnectedNodes(nodeOptions.id).indexOf(nodeId) > -1;
    };

    this.cluster(diagram, data, getClusteredNodePropertiesByNodes, getClusteredEdgePropertiesByEdges, joinCondition, clusterName);
  }

  addNodesToCluster(diagram, data, clusterId, nodeIds, getClusteredNodePropertiesByNodes, getClusteredEdgePropertiesByEdges) {
    const nodesInCurrentCluster = diagram.clustering.getNodesInCluster(clusterId);
    const currentClusterName = data.clusters.find((cluster) => {
      return cluster.id === clusterId;
    }).name;
    nodesInCurrentCluster.push.apply(nodesInCurrentCluster, nodeIds);

    this.openCluster(diagram, data, clusterId);
    this.clusterByIds(diagram,
      data,
      nodesInCurrentCluster,
      getClusteredNodePropertiesByNodes,
      getClusteredEdgePropertiesByEdges,
      currentClusterName);
  }

  clusterByIds(diagram, data, ids, getClusteredNodePropertiesByNodes, getClusteredEdgePropertiesByEdges, clusterName) {
    const joinCondition = (nodeOptions) => {
      return ids.indexOf(nodeOptions.id) !== -1;
    };

    this.cluster(diagram, data, getClusteredNodePropertiesByNodes, getClusteredEdgePropertiesByEdges, joinCondition, clusterName);
  }

  updateClustersProperties(diagram, data, getClusteredNodePropertiesByNodes, getClusteredEdgePropertiesByEdges) {
    this.updateClusteredNodesProperties(diagram, data, getClusteredNodePropertiesByNodes);
    this.updateClusteredEdgesProperties(diagram, data, getClusteredEdgePropertiesByEdges);
  }

  updateClusterProperties(diagram, data, id, name, label) {
    this.updateClusteredLabel(diagram, data, id, name, label);
  }

  openCluster(diagram, data, clusterId) {
    const connectedEdges = [];
    const connectedClusteredEdges = diagram.getConnectedEdges(clusterId);
    connectedClusteredEdges.forEach((edge) => {
      connectedEdges.push.apply(connectedEdges, diagram.clustering.getBaseEdges(edge));
    });

    diagram.openCluster(clusterId, {releaseFunction: this.releaseFunction});
    data.clusters.splice(data.clusters.findIndex((cluster) => {
      return cluster.id === clusterId;
    }), 1);

    connectedEdges.forEach((edgeToFixLabel) => {        // fix labeling
      const labelToRestore = data.edges.get(edgeToFixLabel).label;
      if (labelToRestore !== ' ' && labelToRestore !== '') {
        data.edges.update({id: edgeToFixLabel, label: labelToRestore});
      }
    });
  }

  restoreClusters(diagram, data, nodeClusterVisualProperties, getClusteredEdgePropertiesByEdges) {
    let clusterOptions;
    let clusteredEdges = [];

    data.clusters.forEach((cluster) => {
      clusterOptions = {
        joinCondition(nodeOptions) {
          return cluster.nodes.indexOf(nodeOptions.id) > -1;
        },
        clusterNodeProperties: {
          allowSingleNodeCluster: false,
          x: cluster.x,
          y: cluster.y,
          id: cluster.id,
          label: cluster.label
        },
        clusterEdgeProperties: {
          label: ''
        },
        processProperties(options, nodesInCluster, edgesInCluster) {
          const prop = nodeClusterVisualProperties(nodesInCluster);
          options.image = prop.image;
          options.title = prop.title;
          options.color = {background: prop.color.background, border: prop.color.border};
          clusteredEdges = edgesInCluster;
          return options;
        }
      };

      diagram.cluster(clusterOptions);
    });

    this.updateClusteredEdgesProperties(diagram, data, getClusteredEdgePropertiesByEdges);
  }

  cluster(diagram, data, getClusteredNodePropertiesByNodes, getClusteredEdgePropertiesByEdges, joinCondition, clusterName) {
    let clusteredEdges = [];
    let clusteredNodes = [];
    let nodeForClusterLocation = {x: 0, y: 0};
    const clusterOptions = {
      joinCondition,
      clusterNodeProperties: {
        allowSingleNodeCluster: false,
        label: clusterName
      },
      clusterEdgeProperties: {
        label: ''
      },
      processProperties(options, nodesInCluster, edgesInCluster) {
        const prop = getClusteredNodePropertiesByNodes(nodesInCluster);
        clusteredNodes = nodesInCluster;
        options.image = prop.image;
        options.color = {background: prop.color.background, border: prop.color.border};
        nodeForClusterLocation = data.nodes.get(nodesInCluster[0].id);
        options.x = nodeForClusterLocation.x;
        options.y = nodeForClusterLocation.y;
        clusteredEdges = edgesInCluster;
        return options;
      }
    };

    diagram.cluster(clusterOptions);
    this.updateClusteredEdgesProperties(diagram, data, getClusteredEdgePropertiesByEdges);

    if (clusteredNodes.length > 1) {
      const clusterId = diagram.clustering.findNode(clusteredNodes[0].id)[0];
      const clusteredNodesIds = diagram.getNodesInCluster(clusterId);

      data.clusters.push({
        id: clusterId,
        nodes: clusteredNodesIds,
        x: nodeForClusterLocation.x,
        y: nodeForClusterLocation.y,
        name: clusterName
      });
    }
  }

  releaseFunction(clusterPosition, containedNodesPositions) {
    return containedNodesPositions;
  }

  updateClusteredNodesProperties(diagram, data, getClusteredNodePropertiesByNodes) {
    const allVisibleClusters = diagram.clustering.body.nodeIndices.filter((nodeIndex) => {
      return diagram.isCluster(nodeIndex);
    });

    allVisibleClusters.forEach((cluster) => {
      const nodesInCluster = data.nodes.get(diagram.clustering.getNodesInCluster(cluster));
      const currentClusterProperties = diagram.body.nodes[cluster];
      const calculatedClusterProperties = getClusteredNodePropertiesByNodes(nodesInCluster);
      const currentClusterData = data.clusters.find(clusterItem => clusterItem.id === cluster);

      if (!this.areNodeClustersPropertiesEqual(calculatedClusterProperties, currentClusterProperties) ||
          currentClusterData.label !== currentClusterProperties.options.label) {
          diagram.clustering.updateClusteredNode(cluster, {
            label: currentClusterData.label,
            image: calculatedClusterProperties.image,
            color: calculatedClusterProperties.color,
            title: calculatedClusterProperties.title
          });
      }
    });
  }

  updateClusteredLabel(diagram, data, id, name, label) {
    diagram.clustering.updateClusteredNode(id, {name, label});
    const cluster = data.clusters.find((c) => c.id === id);

    cluster.label = label;
    cluster.name = name;
  }

  updateClusteredEdgesProperties(diagram, data, getClusteredEdgePropertiesByEdges) {
    const allVisibleClusters = diagram.clustering.body.edgeIndices.filter((edgeIndex) => {
      return edgeIndex.indexOf('clusterEdge') > -1;
    });

    allVisibleClusters.forEach((clusteredEdge) => {
      const edgesInCluster = data.edges.get(diagram.clustering.getBaseEdges(clusteredEdge));
      const currentClusterProperties = diagram.body.edges[clusteredEdge];
      const calculatedClusterProperties = getClusteredEdgePropertiesByEdges(edgesInCluster);

      if (!this.areEdgeClustersPropertiesEqual(calculatedClusterProperties, currentClusterProperties)) {
        diagram.clustering.updateEdge(clusteredEdge,
          {
            color: {color: calculatedClusterProperties.color, opacity: calculatedClusterProperties.opacity},
            title: calculatedClusterProperties.title,
            width: calculatedClusterProperties.width,
            dashes: calculatedClusterProperties.dashes
          });
      }
    });
  }

  areNodeClustersPropertiesEqual(calculatedProp, currentProp) {
    if (calculatedProp.image !== currentProp.options.image) {
      return false;
    }
    if (calculatedProp.color.background !== currentProp.options.color.background) {
      return false;
    }

    if (calculatedProp.color.border !== currentProp.options.color.border) {
      return false;
    }

    if (calculatedProp.title !== currentProp.options.title) {
      return false;
    }

    return true;
  }

  areEdgeClustersPropertiesEqual(calculatedProp, currentProp) {
    if (calculatedProp.color !== currentProp.options.color.color) {
      return false;
    }
    if (calculatedProp.opacity !== currentProp.options.color.opacity) {
      return false;
    }

    if (calculatedProp.title !== currentProp.options.title) {
      return false;
    }

    if (calculatedProp.width !== currentProp.options.width) {
      return false;
    }

    if (calculatedProp.dashes !== currentProp.options.dashes) {
      return false;
    }
    return true;
  }
}
